import React, { useRef, useState, useEffect } from "react";
import { 
  Box, 
  Typography, 
  Grid, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableRow,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Snackbar
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { useReactToPrint } from "react-to-print";
import config from "../config/Config";
import axios from "axios";
import { apiHeaderToken } from "../helper/My_Helper";

const GMCForm = ({ candidateData = {}, formData = {} }) => {
  const formRef = useRef();
  const [gmcAgreement, setGmcAgreement] = useState(false);
  const [gmcDateExists, setGmcDateExists] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [gmcDate, setGmcDate] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const appliedJob =
  candidateData?.applied_jobs?.find(
    item => item?.approval_note_data?.note_id  && item?.form_status ==="Offer" || item?.form_status ==="Hired" // priority
  ) ||
  candidateData?.applied_jobs?.find(
    item => item?.job_id === candidateData?.job_id // fallback
  );

  // Check if gmc_add_date exists when component mounts or candidateData changes
  useEffect(() => {
    if (candidateData?._id) {
      checkGmcDateExistence();
    }
  }, [candidateData?._id]);

  const checkGmcDateExistence = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if gmc_add_date exists in candidateData
      if (appliedJob?.gmc_add_date) {
        setGmcDateExists(true);
        setGmcAgreement(true);
        setGmcDate(appliedJob?.gmc_add_date);
        setLoading(false);
        return;
      }
      let response = await axios.post(`${config.API_URL}checkGmcDateExistence`,{candidate_id: candidateData?._id,candidate_doc_id: candidateData?._id,},apiHeaderToken(config.API_TOKEN))
      if (response.data && response.data.success) {
        const exists = response.data.data?.exists || false;
        const gmcDateValue = response.data.data?.gmc_add_date || null;
        
        setGmcDateExists(exists);
        setGmcAgreement(exists);
        setGmcDate(gmcDateValue);
        
        if (exists && gmcDateValue) {
          // Update candidateData with gmc_add_date if not already present
          candidateData.gmc_add_date = gmcDateValue;
        }
      } else {
        setGmcDateExists(false);
        setGmcAgreement(false);
        setGmcDate(null);
      }
    } catch (error) {
      // Default to unchecked if API fails
      setGmcDateExists(false);
      setGmcAgreement(false);
      setGmcDate(null);
    } finally {
      setLoading(false);
    }
  };

  // Show toast message
  const showToast = (message, severity = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  // Handle toast close
  const handleToastClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToastOpen(false);
  };

  // Handle checkbox change - Save GMC Date Details
  const handleGmcAgreementChange = async (event) => {
    const checked = event.target.checked;
    
    // If date already exists, don't allow unchecking
    if (gmcDateExists) {
      showToast("GMC details already submitted. Cannot modify.", "error");
      return;
    }

    setError(null);
    setSuccessMessage(null);
    setIsUpdating(true);

    try {
      setLoading(true);
      
      // Prepare payload with the required body parameters
      const payload = {
        approval_note_doc_id: appliedJob?.approval_note_data?.doc_id || formData?.approval_note_doc_id || candidateData?.approval_note_doc_id || null,
        candidate_doc_id: candidateData?._id || null,
        candidate_id: candidateData?._id,
        gmc_agreement: checked,
        gmc_add_date: checked ? new Date().toISOString() : null,
      };

      let response = await axios.post(`${config.API_URL}saveGMCDateDetailsInCandidateProfile`, payload, apiHeaderToken(config.API_TOKEN))

      if (response.data && response.data.status) {
        setGmcAgreement(checked);
        if (checked) {
          // Get the latest date from the API response or use current date
          const latestDate = response.data.data?.gmc_add_date || new Date().toISOString();
          setGmcDateExists(true);
          setGmcDate(latestDate);
          // Update candidateData with the new date
          candidateData.gmc_add_date = latestDate;
          showToast("GMC details saved successfully!", "success");
        } else {
          setGmcDateExists(false);
          setGmcDate(null);
          showToast("GMC agreement removed successfully.", "info");
        }
        
        setSuccessMessage(response.data.message || "Operation completed successfully");
        
        // Remove the manual refresh requirement - the date is already updated
        // No need to refresh the page
        
      } else {
        // Handle API error
        const errorMsg = response.data?.message || "Failed to save GMC details";
        showToast(errorMsg, "error");
        // Revert checkbox state
        setGmcAgreement(!checked);
      }
    } catch (error) {
      console.error("Error saving GMC details:", error);
      const errorMsg = error.response?.data?.message || "Failed to save GMC details. Please try again.";
      showToast(errorMsg, "error");
      // Revert checkbox state
      setGmcAgreement(!checked);
    } finally {
      setLoading(false);
      setIsUpdating(false);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: formRef,
    pageStyle: `
      @page {
        size: A4;
        margin: 10mm;
      }

      @media print {
        html, body {
          width: 210mm;
          height: 297mm;
        }

        .no-print {
          display: none !important;
        }

        .footer-section {
          page-break-inside: avoid;
        }

        * {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,
  });

  const getCandidateName = () => {
    const fullName = candidateData?.applicant_form_data?.full_name;

    if (typeof fullName === "object") {
      return [
        fullName?.first_name,
        fullName?.middle_name,
        fullName?.surname,
      ]
        .filter(Boolean)
        .join(" ");
    }

    return (
      candidateData?.name ||
      candidateData?.applicant_form_data?.candidate_name ||
      ""
    );
  };

  const getDesignation = () => {
    return (
      appliedJob?.job_designation ||
      candidateData?.job_title ||
      candidateData?.designation ||
      candidateData?.applied_jobs?.[0]?.job_designation ||
      ""
    );
  };

  // Get GMC family members from candidateData
  const getGmcFamilyMembers = () => {
    // Check if gmc_family_members exists in candidateData
    if (candidateData?.gmc_family_members && candidateData.gmc_family_members.length > 0) {
      return candidateData.gmc_family_members;
    }
    
    // Fallback to applied_jobs
    if (appliedJob?.gmc_family_members && appliedJob.gmc_family_members.length > 0) {
      return appliedJob.gmc_family_members;
    }
    
    // Fallback to applicant_form_data family_members
    return candidateData?.applicant_form_data?.family_members || [];
  };

  const gmcFamilyMembers = getGmcFamilyMembers();

  const calculateAge = (dob) => {
    if (!dob) return "";

    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  // Get grade from candidateData
  const getGrade = () => {
    return (
      candidateData?.grade ||
      appliedJob?.grade ||
      candidateData?.applicant_form_data?.grade ||
      formData?.grade ||
      ""
    );
  };

  // Get income limit from candidateData
  const getIncomeLimit = () => {
    return (
      candidateData?.income_limit ||
      appliedJob?.income_limit ||
      formData?.income_limit ||
      "1,500"
    );
  };

  const getDateOfJoining = () => {
    return (
      candidateData?.annexure_eleven_form_data?.candidate_doj || 
      appliedJob?.onboard_date || 
      candidateData?.applicant_form_data?.date_of_joining ||
      ""
    );
  };

  const formatDate = (date) => {
    if (!date) return "";

    try {
      const d = new Date(date);
      if (!isNaN(d.getTime())) {
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
      }
      return "";
    } catch {
      return "";
    }
  };

  // Get the income limit value for display
  const incomeLimitValue = getIncomeLimit();
  const formattedIncomeLimit = typeof incomeLimitValue === 'number' 
    ? incomeLimitValue.toLocaleString() 
    : incomeLimitValue;

  return (
    <Box>
      <Box sx={{ textAlign: "center", mb: 2 }} className="no-print">
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={loading}
        >
          Print Form
        </Button>
        {/* Removed the Refresh button as it's no longer needed */}
      </Box>

      <Box
        ref={formRef}
        sx={{
          backgroundColor: "#fff",
          p: 3,
          fontFamily: "Times New Roman, serif",
          maxWidth: "210mm",
          mx: "auto",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: "relative",
            mb: 3,
            minHeight: "70px",
          }}
        >
          <Box sx={{ position: "absolute", left: 0, top: 0 }}>
            <img
              src={config?.IMAGE_PATH ? config.IMAGE_PATH + "logo512.png" : "/logo512.png"}
              alt="HLFPPT Logo"
              height="55"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/logo512.png";
              }}
            />
          </Box>

          <Typography
            align="center"
            sx={{
              fontSize: "18px",
              fontWeight: "bold",
              letterSpacing: "1px",
              mb: 2,
              mt: 1,
            }}
          >
            Group Medi Claim Policy
          </Typography>
        </Box>

        {/* Introduction */}
        <Typography
          sx={{
            fontSize: "14px",
            mb: 3,
            textAlign: "justify",
          }}
        >
          Under the Group Medi Claim Policy benefit extended by the company to its employees,
          kindly fill-in the following details:
        </Typography>

        {/* Employee Details */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Typography fontWeight="bold" fontSize="14px">
              Name of the Employee
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #000",
                minHeight: "30px",
                mb: 2,
              }}
            >
              <Typography fontSize="14px">{getCandidateName()}</Typography>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Typography fontWeight="bold" fontSize="14px">
              Designation of the Employee
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #000",
                minHeight: "30px",
                mb: 2,
              }}
            >
              <Typography fontSize="14px">{getDesignation()}</Typography>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Typography fontWeight="bold" fontSize="14px">
              Grade of the Employee
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #000",
                minHeight: "30px",
                mb: 2,
              }}
            >
              <Typography fontSize="14px">{getGrade()}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography fontWeight="bold" fontSize="14px">
              Date of Joining
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #000",
                minHeight: "30px",
                mb: 2,
              }}
            >
              <Typography fontSize="14px">
                {formatDate(getDateOfJoining())}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Employee Declaration */}
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          Employee Declaration
        </Typography>

        <Typography
          sx={{
            fontSize: "14px",
            mb: 2,
            textAlign: "justify",
          }}
        >
          I hereby declare that under the Group Medi Claim Policy provided by the company,
          the following are the details of the members of my family to be covered
        </Typography>

        {/* Family Members Table - Dynamic from gmc_family_members */}
        <TableContainer sx={{ mb: 3, border: "1px solid #000" }}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ border: "1px solid #000", fontWeight: "bold", p: 1 }}>
                  Name
                </TableCell>
                <TableCell sx={{ border: "1px solid #000", fontWeight: "bold", p: 1 }}>
                  Relation
                </TableCell>
                <TableCell sx={{ border: "1px solid #000", fontWeight: "bold", p: 1 }}>
                  Date of Birth
                </TableCell>
                <TableCell sx={{ border: "1px solid #000", fontWeight: "bold", p: 1 }}>
                  Age
                </TableCell>
                <TableCell sx={{ border: "1px solid #000", fontWeight: "bold", p: 1 }}>
                  Gender
                </TableCell>
                <TableCell sx={{ border: "1px solid #000", fontWeight: "bold", p: 1 }}>
                  Whether dependant or not
                </TableCell>
              </TableRow>

              {/* Self Row */}
              <TableRow>
                <TableCell sx={{ border: "1px solid #000", p: 1 }}>
                  {getCandidateName()}
                </TableCell>
                <TableCell sx={{ border: "1px solid #000", p: 1 }}>
                  Self
                </TableCell>
                <TableCell sx={{ border: "1px solid #000", p: 1 }}>
                  {formatDate(
                    candidateData?.annexure_eleven_form_data?.candidate_dob ||
                      candidateData?.applicant_form_data?.date_of_birth ||
                      candidateData?.applicant_form_data?.dob?.date
                  )}
                </TableCell>
                <TableCell sx={{ border: "1px solid #000", p: 1 }}>
                  {calculateAge(
                    candidateData?.annexure_eleven_form_data?.candidate_dob ||
                      candidateData?.applicant_form_data?.date_of_birth ||
                      candidateData?.applicant_form_data?.dob?.date
                  )}
                </TableCell>
                <TableCell sx={{ border: "1px solid #000", p: 1 }}>
                  {candidateData?.applicant_form_data?.gender || ""}
                </TableCell>
                <TableCell sx={{ border: "1px solid #000", p: 1 }}>
                  N/A
                </TableCell>
              </TableRow>

              {/* Dynamic Family Members from gmc_family_members */}
              {gmcFamilyMembers.length > 0 ? (
                gmcFamilyMembers.map((member, index) => (
                  <TableRow key={member._id || index}>
                    <TableCell sx={{ border: "1px solid #000", p: 1 }}>
                      {member?.name || ""}
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #000", p: 1 }}>
                      {member?.relation || member?.particulars || ""}
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #000", p: 1 }}>
                      {formatDate(member?.dateOfBirth || member?.date_of_birth)}
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #000", p: 1 }}>
                      {member?.age || calculateAge(member?.dateOfBirth || member?.date_of_birth)}
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #000", p: 1 }}>
                      {member?.gender || 
                        (member?.relation?.toLowerCase() === "father" ? "Male" :
                         member?.relation?.toLowerCase() === "mother" ? "Female" :
                         member?.particulars?.toLowerCase() === "father" ? "Male" :
                         member?.particulars?.toLowerCase() === "mother" ? "Female" : "")}
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #000", p: 1 }}>
                      {member?.isDependent === "yes" || member?.isDependent === true ? "Yes" : 
                       member?.is_dependent === "yes" ? "Yes" : "No"}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                // Fallback to family_members if no gmc_family_members
                (candidateData?.applicant_form_data?.family_members || []).map((member, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ border: "1px solid #000", p: 1 }}>
                      {member?.name || ""}
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #000", p: 1 }}>
                      {member?.particulars || ""}
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #000", p: 1 }}>
                      {formatDate(member?.date_of_birth)}
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #000", p: 1 }}>
                      {member?.age || calculateAge(member?.date_of_birth)}
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #000", p: 1 }}>
                      {member?.gender ||
                        (member?.particulars?.toLowerCase() === "father" ? "Male" :
                         member?.particulars?.toLowerCase() === "mother" ? "Female" : "")}
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #000", p: 1 }}>
                      {member?.is_dependent === "yes" ? "Yes" : "No"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* GMC Agreement Section */}
        <Box sx={{ 
          mt: 3, 
          mb: 3, 
          p: 2, 
        }}>
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Checking GMC details status...</Typography>
            </Box>
          )}

          {!loading && !gmcDateExists && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={gmcAgreement}
                  onChange={handleGmcAgreementChange}
                  disabled={gmcDateExists || loading || isUpdating}
                />
              }
              label={
                <Typography
                  variant="body1"
                  sx={{
                    backgroundColor: "blue",
                    color: "white",
                    padding: "4px 8px",
                    borderRadius: "4px",
                  }}
                >
                  I agree to the Group Medi Claim Policy terms and conditions
                </Typography>
              }
            />
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccessMessage(null)}>
              {successMessage}
            </Alert>
          )}
        </Box>

        {/* Parent Declaration with dynamic income limit */}
        <Typography
          sx={{
            fontSize: "14px",
            mb: 1,
            textAlign: "justify",
          }}
        >
          I hereby also declare that my parents are dependent on me and their monthly income
          from all sources put together is less than Rs {formattedIncomeLimit}/- per month.
        </Typography>

        <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
          <Typography fontSize="14px">
            {formData?.parents_declaration === "yes" 
              ? `✓ I confirm that my parents are dependent on me and their monthly income is less than Rs ${formattedIncomeLimit}/-` 
              : formData?.parents_declaration === "no" 
              ? "✗ I do not confirm the above declaration" 
              : ""}
          </Typography>
        </Box>

        {/* Signature Section */}
        <Box className="footer-section" sx={{ mt: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Typography fontWeight="bold" fontSize="14px" mb={1}>
                Date
              </Typography>
              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  minHeight: "30px",
                }}
              >
                <Typography fontSize="14px">
                  {formatDate(gmcDate || formData?.date) || ""}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Typography fontWeight="bold" fontSize="14px" mb={1}>
                Signature of Employee
              </Typography>
              <Box
                sx={{
                  minHeight: "50px",
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                {candidateData?.applicant_form_data?.signature && (
                  <img
                    src={`${config?.IMAGE_PATH || ""}${
                      candidateData.applicant_form_data.signature
                    }`}
                    alt="employee-signature"
                    style={{
                      maxHeight: 40,
                      objectFit: "contain",
                    }}
                  />
                )}
              </Box>
              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: "12px",
                  fontStyle: "italic",
                }}
              >
                {getCandidateName()}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Toast Message */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={5000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleToastClose} 
          severity={toastSeverity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GMCForm;